import {
  AccountBalance,
  Business,
  CreditCard,
  Equalizer,
  Flight,
  FlightTakeoff,
  Login as LoginIcon,
  Logout as LogoutIcon,
  Person,
  Public,
  Receipt as ReceiptIcon,
  Repeat,
  StickyNote2,
} from '@mui/icons-material'
import { useMemo, useRef } from 'react'
import { useLocation } from 'react-router'
import type { TButtonLinkProps } from '@/helpers/compositions/ButtonLink'
import { T } from '@/helpers/T'

//  ///////////////////////
//  MARK: CONFIG
export const ACTIONS: {
  [key: string]: TComponentsQuickMenuAction[]
} = {
  '*': [],
  '/entities': [
    { to: '/entities/person/new', startIcon: <Person />, children: <T id='lang.person' /> },
    {
      to: '/entities/organisation/new',
      startIcon: <Business />,
      children: <T id='lang.organisation' />,
    },
  ],
  '/accounting': [
    {
      to: '/accounting/purchase/expense/new',
      startIcon: <LogoutIcon />,
      children: <T id='lang.expense' />,
    },
    {
      to: '/accounting/sale/invoice/new',
      startIcon: <LoginIcon />,
      children: <T id='lang.invoice' />,
    },
    {
      to: '/accounting/payment-mean/bank-account/new',
      startIcon: <AccountBalance />,
      children: <T id='lang.bank_account' />,
    },
    {
      to: '/accounting/payment-mean/credit-card/new',
      startIcon: <CreditCard />,
      children: <T id='lang.credit_card' />,
    },
    {
      to: '/accounting/payment-mean/domiciliation/new',
      startIcon: <Repeat />,
      children: <T id='lang.domiciliation' />,
    },
  ],
  '/human-resources': [
    {
      to: '/human-resources/crew-type/new',
      startIcon: <Equalizer />,
      children: <T id='lang.crew_type' />,
    },
    {
      to: '/human-resources/expense-draft/new',
      startIcon: <ReceiptIcon />,
      children: <T id='lang.expense_draft' />,
    },
  ],
  '/operations': [
    { to: '/operations/quote/new', startIcon: <StickyNote2 />, children: <T id='lang.quote' /> },
  ],
  '/settings': [
    { to: '/settings/country/new', startIcon: <Public />, children: <T id='lang.country' /> },
    {
      to: '/settings/airport/new',
      startIcon: <FlightTakeoff />,
      children: <T id='lang.airport' />,
    },
    {
      to: '/settings/aircraft/new',
      startIcon: <Flight />,
      children: <T id='lang.aircraft' />,
    },
    {
      to: '/settings/aircraft-type/new',
      startIcon: <Flight />,
      children: <T id='lang.aircraft_type' />,
    },
  ],
}

//	///////////////////////
//	MARK: TYPE
export type TComponentsQuickMenuAction = TButtonLinkProps | React.FC

export type TComponentsQuickMenuActionResult = {
  key: string
  content: TComponentsQuickMenuAction
}

//	///////////////////////
//	MARK: EXPORTS
export const useQuickActionsMenu = (reversed = false): TComponentsQuickMenuActionResult[] => {
  const location = useLocation()

  const globalKey = useRef(0)

  return useMemo(() => {
    const result: TComponentsQuickMenuAction[] = []

    for (const [key, values] of Object.entries(ACTIONS)) {
      if (key === '*' || location.pathname.startsWith(key)) {
        result.push(...values)
      }
    }

    globalKey.current += 1

    const value: TComponentsQuickMenuActionResult[] = result.map((e, i) => ({
      content: e,
      key: `${globalKey.current}-${i}`,
    }))

    return reversed ? value : value.reverse()
  }, [location.pathname, reversed])
}
